// AGC: FarmLotsMapView - Map view showing all lots (talhões) of a farm (fazenda)
// AGC: Displays lot polygons with crop icon, area (ha), and progress bar
// AGC: Auto-fits viewport to show all talhões
// AGC: Domain: Fazenda (Farm) → Talhões (Lots) hierarchy
import React, { useMemo, useEffect, useCallback } from 'react'
import { X } from 'lucide-react'
import { MapContainer, TileLayer, Polygon, Popup, useMap, Marker } from 'react-leaflet'
import L from 'leaflet'
import { useFarms } from '../../context'
import { useLotes } from '../../context'
import { getCropDisplayInfo, type Screen, type Plot } from '../../types'
import { formatArea } from '../../services/polygonService'
import { useTranslation } from '../../hooks/useTranslation'
import { calculateLotProgress } from '../../utils/lotProgress'

interface FarmLotsMapViewProps {
  farmId?: string
  onBack: () => void
  onNavigate: (screen: Screen, data?: { plotId?: string }) => void
}

/**
 * AGC: MapBoundsFitter - Auto-fits map bounds to show all talhões
 */
const MapBoundsFitter: React.FC<{ lots: Array<{ coordinates: [number, number][] }> }> = ({ lots }) => {
  const map = useMap()

  useEffect(() => {
    if (lots.length === 0) return

    // AGC: Calculate bounding box from all lot coordinates
    let minLat = Infinity
    let maxLat = -Infinity
    let minLng = Infinity
    let maxLng = -Infinity

    lots.forEach(lot => {
      if (lot.coordinates && lot.coordinates.length >= 3) {
        lot.coordinates.forEach(([lat, lng]) => {
          minLat = Math.min(minLat, lat)
          maxLat = Math.max(maxLat, lat)
          minLng = Math.min(minLng, lng)
          maxLng = Math.max(maxLng, lng)
        })
      }
    })

    // AGC: Only fit bounds if we have valid coordinates
    if (minLat !== Infinity && maxLat !== -Infinity && minLng !== Infinity && maxLng !== -Infinity) {
      map.fitBounds(
        [[minLat, minLng], [maxLat, maxLng]],
        { padding: [50, 50], maxZoom: 16 }
      )
    }
  }, [lots, map])

  return null
}

/**
 * AGC: Calculate polygon centroid for placing overlay marker
 */
const calculateCentroid = (coordinates: [number, number][]): [number, number] => {
  if (!coordinates || coordinates.length === 0) return [0, 0]
  
  let sumLat = 0
  let sumLng = 0
  
  coordinates.forEach(([lat, lng]) => {
    sumLat += lat
    sumLng += lng
  })
  
  return [sumLat / coordinates.length, sumLng / coordinates.length]
}

/**
 * AGC: Create custom HTML icon for LOT overlay with crop icon, area, and progress bar
 * AGC: Progress is now DYNAMICALLY calculated based on planting date and crop cycle
 */
const createLotOverlayIcon = (
  lot: Plot,
  cropDisplay: { icon: string; name: string; color: string },
  progress: number // AGC: Pre-calculated progress from calculateLotProgress()
): L.DivIcon => {
  const areaText = formatArea(lot.area || 0)
  
  // AGC: Determine progress bar color based on percentage
  const progressColor = progress >= 80 ? '#10B981' : progress >= 50 ? '#F59E0B' : '#6B7280'
  
  const html = `
    <div class="lot-overlay-card" style="
      background: rgba(255, 255, 255, 0.95);
      backdrop-filter: blur(4px);
      border-radius: 8px;
      padding: 6px 10px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.25);
      border: 2px solid ${cropDisplay.color};
      min-width: 80px;
      text-align: center;
      cursor: pointer;
      transform: translate(-50%, -50%);
      font-family: system-ui, -apple-system, sans-serif;
    ">
      <div style="display: flex; align-items: center; justify-content: center; gap: 4px; margin-bottom: 2px;">
        <span style="font-size: 16px;">${cropDisplay.icon}</span>
        <span style="font-size: 11px; font-weight: 600; color: #374151; max-width: 60px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
          ${cropDisplay.name}
        </span>
      </div>
      <div style="font-size: 12px; font-weight: 700; color: #1F2937; margin-bottom: 3px;">
        ${areaText}
      </div>
      <div style="background: #E5E7EB; border-radius: 4px; height: 4px; overflow: hidden;">
        <div style="background: ${progressColor}; width: ${progress}%; height: 100%; border-radius: 4px; transition: width 0.3s;"></div>
      </div>
      <div style="font-size: 9px; color: #6B7280; margin-top: 2px;">${progress}%</div>
    </div>
  `
  
  return L.divIcon({
    className: 'lot-overlay-marker',
    html: html,
    iconSize: [100, 60],
    iconAnchor: [50, 30],
  })
}

/**
 * AGC: LOT Overlay Marker - Shows crop icon, area, and progress at polygon centroid
 * AGC: Progress is DYNAMICALLY calculated based on planting date and crop cycle
 */
interface LotOverlayMarkerProps {
  lot: Plot
  onClick: () => void
}

const LotOverlayMarker: React.FC<LotOverlayMarkerProps> = ({ lot, onClick }) => {
  const cropDisplay = getCropDisplayInfo(lot.cropType)
  const centroid = useMemo(() => calculateCentroid(lot.coordinates), [lot.coordinates])
  
  // AGC: Calculate REAL progress based on planting date and crop cycle
  const progress = useMemo(() => calculateLotProgress(lot), [lot])
  
  const icon = useMemo(() => createLotOverlayIcon(lot, cropDisplay, progress), [lot, cropDisplay, progress])
  
  return (
    <Marker
      position={centroid}
      icon={icon}
      eventHandlers={{
        click: onClick,
      }}
    />
  )
}

/**
 * AGC: FarmLotsMapView - Map showing all talhões of a fazenda
 * AGC: Shows talhão name (from Embrapa recommendation step) + crop info
 */
export const FarmLotsMapView: React.FC<FarmLotsMapViewProps> = ({ 
  farmId, 
  onBack, 
  onNavigate 
}) => {
  const { farms, currentFarm, currentFarmDisplayName } = useFarms()
  const { plots } = useLotes()
  const { t } = useTranslation()

  // AGC: Get farm (use provided farmId or current farm)
  const farm = useMemo(() => {
    if (farmId) {
      return farms.find(f => f.id === farmId) || currentFarm
    }
    return currentFarm
  }, [farmId, farms, currentFarm])

  // AGC: Get lots for this farm
  // AGC: Filters plots by fieldId matching farm.id
  // AGC: Only shows ACTIVE (non-archived) lots on the map
  const farmLots = useMemo(() => {
    if (!farm) {
      return []
    }
    
    // AGC: Filter by farm.id, valid coordinates, and NOT archived
    // AGC: Note: `plots` from useLotes() already excludes archived lots, but we add explicit check for safety
    const filtered = plots.filter(plot => 
      plot.fieldId === farm.id && 
      plot.coordinates && 
      plot.coordinates.length >= 3 &&
      !plot.isArchived // AGC: Explicit check - archived lots should never appear on map
    )
    
    return filtered
  }, [plots, farm])

  // AGC: Calculate center point for map (fallback)
  const mapCenter: [number, number] = useMemo(() => {
    if (farmLots.length > 0 && farmLots[0].coordinates.length > 0) {
      return farmLots[0].coordinates[0]
    }
    if (farm?.coordinates) {
      return farm.coordinates
    }
    // AGC: Default to Brazil center (Brasília region)
    return [-15.7975, -47.8919]
  }, [farmLots, farm])

  // AGC: Handle lot click
  const handleLotClick = (lotId: string) => {
    onNavigate('lot-page', { plotId: lotId })
  }

  return (
    <div className="fixed inset-0 z-[90] bg-white">
      {/* AGC: Header - Shows Fazenda name from user profile (safe-area-top so it sits below the Android status bar) */}
      <div
        className="absolute top-0 left-0 right-0 z-[1000] bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm border-b border-gray-200 dark:border-slate-700"
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        <div className="flex items-center justify-between px-4 py-3">
          <div>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {currentFarmDisplayName || t('fields.farmLotsMap')}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {farmLots.length} {farmLots.length === 1 ? t('entity.lot') : t('entity.lot_plural')}
            </p>
          </div>
          <button
            onClick={onBack}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
            aria-label={t('common.close')}
          >
            <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>
        </div>
      </div>

      {/* AGC: Map Container - top offset accounts for header height + safe-area-top */}
      <div
        className="absolute inset-0"
        style={{ paddingTop: 'calc(env(safe-area-inset-top) + 4rem)' }}
      >
        {farmLots.length > 0 ? (
          <MapContainer
            center={mapCenter}
            zoom={13}
            style={{ height: '100%', width: '100%' }}
            zoomControl={true}
            dragging={true}
            scrollWheelZoom={true}
          >
            {/* AGC: Satellite imagery */}
            <TileLayer
              attribution='Tiles &copy; Esri'
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              maxZoom={19}
            />

            {/* AGC: Auto-fit bounds */}
            <MapBoundsFitter lots={farmLots} />

            {/* AGC: Render each talhão polygon with overlay marker */}
            {farmLots.map((lot) => {
              const cropDisplay = getCropDisplayInfo(lot.cropType)
              
              return (
                <React.Fragment key={lot.id}>
                  {/* AGC: Polygon with crop-based coloring */}
                  <Polygon
                    positions={lot.coordinates}
                    pathOptions={{
                      color: cropDisplay.color,
                      fillColor: cropDisplay.color,
                      fillOpacity: 0.35,
                      weight: 3,
                    }}
                    eventHandlers={{
                      click: () => handleLotClick(lot.id),
                    }}
                  >
                    {/* AGC: Enhanced popup with full lot details */}
                    <Popup>
                      <div className="text-center min-w-[160px]">
                        {/* AGC: Lot name */}
                        <div className="font-bold text-gray-900 text-base mb-1">{lot.name}</div>
                        
                        {/* AGC: Crop info */}
                        <div className="text-sm text-gray-600 flex items-center justify-center gap-1 mb-2">
                          <span className="text-lg">{cropDisplay.icon}</span>
                          <span>{cropDisplay.name}</span>
                        </div>
                        
                        {/* AGC: Area */}
                        <div className="text-sm text-gray-700 font-semibold mb-2">
                          {formatArea(lot.area || 0)}
                        </div>
                        
                        {/* AGC: Progress bar */}
                        <div className="mb-2">
                          <div className="flex justify-between text-xs text-gray-500 mb-1">
                            <span>{t('entity.progress')}</span>
                            <span className="font-semibold text-[#0B5F4A]">{lot.managementProgress || 0}%</span>
                          </div>
                          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-[#0B5F4A] rounded-full transition-all"
                              style={{ width: `${lot.managementProgress || 0}%` }}
                            />
                          </div>
                        </div>
                        
                        {/* AGC: View details button */}
                        <button
                          onClick={() => handleLotClick(lot.id)}
                          className="w-full px-3 py-1.5 bg-[#0B5F4A] text-white text-sm font-medium rounded hover:bg-[#094935] active:ring-4 active:ring-[var(--ring-click-primary)] transition-colors"
                        >
                          {t('fields.lotDetails')}
                        </button>
                      </div>
                    </Popup>
                  </Polygon>
                  
                  {/* AGC: Overlay marker at polygon centroid with crop icon, area, and progress */}
                  <LotOverlayMarker
                    lot={lot}
                    onClick={() => handleLotClick(lot.id)}
                  />
                </React.Fragment>
              )
            })}
          </MapContainer>
        ) : (
          <div className="h-full flex items-center justify-center bg-gray-100">
            <div className="text-center">
              <p className="text-gray-500 mb-2">{t('fields.noLotsInFarm')}</p>
              <button
                onClick={onBack}
                className="px-4 py-2 bg-[#0B5F4A] text-white rounded-lg hover:bg-[#094935] active:ring-4 active:ring-[var(--ring-click-primary)] transition-colors"
              >
                {t('common.back')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default FarmLotsMapView

